import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { Store } from '@ngxs/store';

import { ApolloRoutes } from '@app/client/client.module';
import { languages, params } from '@app/configurations';
import { STORAGE_KEYS } from '@app/core/models/storage-keys.models';
import { TranslateService } from '@ngx-translate/core';
import { getDefaultLanguage } from '../config/locale.config';
import { Customer } from '../models/customer.models';
import { GetAllAccounts } from '../store/states/accounts/accounts-state.actions';
import { AccountsState } from '../store/states/accounts/accounts.state';
import { GetCustomerCurrencyRate } from '../store/states/currency/currency-state.actions';
import { GetCustomer } from '../store/states/customer/customer-state.actions';
import { CustomerState } from '../store/states/customer/customer.state';
import { GoToAgencyAdmin } from '../store/states/router-nav/router-nav-state.actions';
import { GetUser, GetUserSettings } from '../store/states/user/user-state.actions';
import { IsBilledApp, IsPortalApp, IsSignupApp } from '../utilities/app.utilities';
import { getCookieByKey, SetBrowserAgentClass, SetOSClass } from '../utilities/dom.utilities';
import { GetOAuthPath, sanitizeNullQueryParams, setPortalParams } from '../utilities/url.utilities';

export const initializeAppFactory = (store: Store, translate: TranslateService) => () => {
  if (IsPortalApp()) {
    setPortalParams();
  } else {
    const params = sanitizeNullQueryParams(); // remove null query Params
    if (params) {
      location.search = `?${params.join('&')}`;
      return EMPTY;
    }
  }

  // Setup - DOM utilities
  SetBrowserAgentClass();
  SetOSClass();
  // Setup - translation
  translate.addLangs(Object.values(languages).map((l) => l.toLowerCase()));

  const val = getDefaultLanguage();
  if (val) translate.setDefaultLang(val);

  const signUpSync = localStorage.getItem(STORAGE_KEYS.SIGN_UP_SYNC);
  if (signUpSync) {
    localStorage.removeItem(STORAGE_KEYS.SIGN_UP_SYNC);
    localStorage.removeItem(STORAGE_KEYS.CUSTOMER_ID);
    location.replace(`${location.origin}/signup/syncing`);
    return EMPTY;
  }

  if (IsBilledApp() || IsPortalApp()) return EMPTY;

  const hasXSRFCookie = getCookieByKey('xsrf');
  // when in signup and there is no xsrf cookie, it means there was no previous login
  if (IsSignupApp() && !hasXSRFCookie) return EMPTY;

  // Main dispatch flow
  return store
    .dispatch([new GetUser(), ...(IsSignupApp() ? [] : [new GetUserSettings(), new GetCustomerCurrencyRate()])])
    .pipe(
      switchMap(() => store.dispatch(new GetCustomer())),
      switchMap(() =>
        store
          .selectOnce(CustomerState.customer)
          .pipe(
            switchMap((customer) =>
              customer && !IsSignupApp() && !IsPortalApp() ? checkAccountsCustomerAccess(store, customer) : of(void 0),
            ),
          ),
      ),
      catchError(() => of(void 0)),
    );
};

function checkAccountsCustomerAccess(store: Store, customer: Customer): Observable<void> {
  return store.dispatch(new GetAllAccounts(customer.customerId)).pipe(
    switchMap(() =>
      combineLatest([store.selectOnce(AccountsState.hasAccounts), store.selectOnce(CustomerState.isAgencyAdmin)]),
    ),
    map(([hasAccounts, isAgencyAdmin]) => {
      if (!hasAccounts && isAgencyAdmin) {
        if (
          ApolloRoutes.some(
            (route) => location.pathname.startsWith(`/${route.path}`) && route.data?.noEmptyAgencyAccess,
          )
        ) {
          store.dispatch(new GoToAgencyAdmin(customer));
        }
      }
    }),
  );
}

export function transformLegacyParamsFactory(): () => Promise<any> {
  return (): Promise<any> =>
    new Promise((resolve) => {
      // for google-mc oauth
      if (location.href.includes('/oauth/google-mc/')) {
        window.history.replaceState(null, null, GetOAuthPath(params.CUSTOMER_ID, 'google-mc'));
      }

      // for amazon-sp oauth
      else if (location.href.includes('/oauth/amazon-sp/')) {
        window.history.replaceState(null, null, GetOAuthPath(params.CUSTOMER_ID, 'amazon-sp'));
      }

      resolve(true);
    });
}
